import React, { useState, useEffect } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';
import { MergeView } from './tools/MergeView';
import { SplitView } from './tools/SplitView';
import { RotateView } from './tools/RotateView';
import { WatermarkView } from './tools/WatermarkView';
import { ProtectView } from './tools/ProtectView';
import { JpgToPdfView } from './tools/JpgToPdfView';
import { PageNumbersView } from './tools/PageNumbersView';
import { CropPdfView } from './tools/CropPdfView';
import { RepairPdfView } from './tools/RepairPdfView';
import { Base64PdfView } from './tools/Base64PdfView';
import { PdfToJpgView } from './tools/PdfToJpgView';
import { HtmlToPdfView } from './tools/HtmlToPdfView';
import { MarkdownPdfView } from './tools/MarkdownPdfView';
import { PdfToPdfAView } from './tools/PdfToPdfAView';
import { useAppStore } from '../store/useAppStore';
import './Layout.css';

export type ToolTab = 'merge' | 'split' | 'rotate' | 'watermark' | 'protect' | 'jpgToPdf' | 'pageNumbers' | 'crop' | 'repair' | 'base64' | 'pdfToJpg' | 'htmlToPdf' | 'markdown' | 'sanitize';

export const Layout: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ToolTab>('merge');
  const resolvedTheme = useAppStore((state) => state.resolvedTheme);
  const theme = useAppStore((state) => state.theme);

  useEffect(() => {
    // Sync active theme class/attribute
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    
    // Update mobile/PWA browser header bar color
    requestAnimationFrame(() => {
      const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--color-bg-primary').trim();
      if (bgColor) {
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', bgColor);
      }
    });
  }, [resolvedTheme]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        const resolved = mediaQuery.matches ? 'dark' : 'light';
        useAppStore.setState({ resolvedTheme: resolved });
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => { mediaQuery.removeEventListener('change', handleChange); };
  }, [theme]);

  const renderActiveView = () => {
    switch (activeTab) {
      case 'merge': return <MergeView />;
      case 'split': return <SplitView />;
      case 'rotate': return <RotateView />;
      case 'watermark': return <WatermarkView />;
      case 'protect': return <ProtectView />;
      case 'jpgToPdf': return <JpgToPdfView />;
      case 'pageNumbers': return <PageNumbersView />;
      case 'crop': return <CropPdfView />;
      case 'repair': return <RepairPdfView />;
      case 'base64': return <Base64PdfView />;
      case 'pdfToJpg': return <PdfToJpgView />;
      case 'htmlToPdf': return <HtmlToPdfView />;
      case 'markdown': return <MarkdownPdfView />;
      case 'sanitize': return <PdfToPdfAView />;
      default: return <MergeView />;
    }
  };

  return (
    <div className="layout-container">
      <Header activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="layout-main">
        {renderActiveView()}
      </main>
      <Footer />
    </div>
  );
};
